import { PricingAdvantageTooltip } from '@/components/PricingAdvantageTooltip';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Info, Sparkles } from 'lucide-react';
import Image from 'next/image';

interface PricingComparisonCardProps {
  currentPlanId: string | null;
  isYearly: boolean;
  isWeek: boolean;
  activePlan: any;
  currentCredits: number;
  displayPrice: number;
  currencySymbol: string;
  selectedPlan: number;
  OPENAI_COST_PER_VIDEO: number;
}

export function PricingComparisonCard({
  currentPlanId,
  isYearly,
  isWeek,
  activePlan,
  currentCredits,
  displayPrice,
  currencySymbol,
  selectedPlan,
  OPENAI_COST_PER_VIDEO,
}: PricingComparisonCardProps) {
  return (
    <Card
      className={cn(
        'relative w-full overflow-visible border border-zinc-300 bg-white shadow-none transition-shadow dark:border-zinc-600 dark:bg-[#2f2f2f]',
        currentPlanId ? 'mt-8' : 'mt-8',
        'rounded-3xl'
      )}
    >
      <div className="-top-[14px] -translate-x-1/2 absolute left-1/2 z-30 flex items-center justify-center">
        {(() => {
          const c = isYearly
            ? activePlan?.credits?.yearly || 0
            : isWeek
              ? activePlan?.credits?.weekly || 0
              : activePlan?.credits?.monthly || 0;
          const planVideos = Math.floor(c / 30);
          const p = isYearly ? activePlan?.yearlyPrice || 0 : activePlan?.price || 0;
          const openaiVideos = Math.round(p / OPENAI_COST_PER_VIDEO);

          return (
            <div className="flex h-auto min-h-[30px] w-auto max-w-[calc(100vw-2rem)] flex-nowrap items-center justify-center gap-1 rounded-lg border border-zinc-200 bg-zinc-100 px-2 py-1 shadow-sm dark:border-[rgba(255,255,255,0.1)] dark:bg-zinc-900">
              <span className="whitespace-nowrap font-bold text-[11px] text-gray-900 uppercase tracking-tight dark:text-white">
                {(planVideos - openaiVideos).toLocaleString()} MORE{' '}
                {planVideos - openaiVideos === 1 ? 'VIDEO' : 'VIDEOS'}
              </span>
              <span className="whitespace-nowrap font-medium text-[11px] text-gray-500 uppercase tracking-tight dark:text-zinc-400">
                vs OpenAI
              </span>
              <PricingAdvantageTooltip
                planCredits={currentCredits}
                planVideos={planVideos}
                costPerCredit={(displayPrice || 0) / currentCredits}
                currencySymbol={currencySymbol}
              >
                <button
                  type="button"
                  aria-label="See pricing comparison details"
                  className="inline-flex cursor-pointer items-center justify-center rounded-full bg-gray-200/60 p-0.5 transition-colors hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                >
                  <Info className="h-3 w-3 text-gray-500" aria-hidden="true" />
                </button>
              </PricingAdvantageTooltip>
            </div>
          );
        })()}
      </div>
      <CardContent className="px-4 pt-4 pb-1">
        <div className="w-full">
          {(() => {
            const c = isYearly
              ? activePlan?.credits?.yearly || 0
              : isWeek
                ? activePlan?.credits?.weekly || 0
                : activePlan?.credits?.monthly || 0;
            const planVideos = Math.floor(c / 30);
            const p = isYearly ? activePlan?.yearlyPrice || 0 : activePlan?.price || 0;
            const openaiVideos = Math.round(p / OPENAI_COST_PER_VIDEO);
            const percentMore = Math.round(((planVideos - openaiVideos) / openaiVideos) * 100);

            return (
              <div className="flex flex-col gap-2.5 text-xs">
                <div className="flex flex-col gap-2">
                  <div
                    className="relative h-12 overflow-hidden py-1"
                    key={`zapping-bar-${selectedPlan}`}
                  >
                    <div className="absolute inset-0 flex animate-bar-bounce items-center justify-between rounded-md border border-zinc-200 bg-gray-100 px-2 dark:border-white/40 dark:bg-zinc-800">
                      <div className="-translate-y-[1px] !rounded-none relative h-[23px] w-[74px] overflow-visible">
                        <Image
                          src="/images/zappingai-logo-small.png"
                          alt="ZappingAI"
                          fill
                          sizes="74px"
                          className="!rounded-none object-contain object-left dark:brightness-0 dark:invert"
                          unoptimized
                        />
                      </div>
                      <span className="flex items-center gap-1 font-bold text-[11px] text-gray-900 uppercase tracking-tighter sm:gap-2 sm:text-[14px] dark:text-gray-100">
                        <Sparkles
                          className="h-3 w-3 fill-indigo-500 text-indigo-500 sm:h-3.5 sm:w-3.5"
                          aria-hidden="true"
                        />
                        {percentMore}% EXTRA: {planVideos.toLocaleString()}
                      </span>
                      <div className="-translate-x-full pointer-events-none absolute inset-0 animate-[shimmer_4s_infinite] bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                    </div>
                  </div>

                  <div className="relative h-10 overflow-hidden py-1">
                    <div
                      className="absolute inset-y-1 left-0 flex items-center justify-between rounded-md border border-zinc-200 bg-gray-100 px-2 transition-all duration-700 dark:border-white/40 dark:bg-zinc-800"
                      style={{
                        width: `calc(${Math.min((openaiVideos / planVideos) * 100, 100)}% - 8px)`,
                      }}
                    >
                      <div className="!rounded-none relative h-[10.5px] w-[34px] translate-y-[2px] overflow-visible opacity-80">
                        <Image
                          src="/images/openai.png"
                          alt="OpenAI"
                          fill
                          sizes="34px"
                          className="!rounded-none object-contain object-left dark:brightness-0 dark:invert"
                        />
                      </div>
                      <span className="font-bold text-[11px] text-gray-900 opacity-90 sm:text-[14px] dark:text-gray-100">
                        {openaiVideos.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
}
